/**
 * The owner's rule for every table in the app: a column that is 0, null,
 * undefined, '' or '—' on EVERY row carries no information and is hidden,
 * and the table note says which columns went, so a reader never wonders
 * where "Launch" is (the retired launch model rendered "$0.00" on every
 * linked row under a note saying the model is retired - R33).
 *
 * Invariants:
 *  - pure: the rows are only read, the column list only filtered; order is
 *    kept, so colSpans computed from `shown.length` line up with the cells.
 *  - a column is hidden only when EVERY row is empty for it: one real value
 *    keeps the whole column, because a hidden real value is a wrong table.
 *  - no rows hides nothing: an empty table is a fact about the data, not
 *    about the columns, and "Hidden: everything" would be nonsense.
 *  - NaN is not empty: a NaN is a bug that must stay visible, never tidied
 *    away by the rule.
 *  - `fixed` columns (identity, controls) are exempt: the rule is about data
 *    columns, and a table with no Feed column cannot be read.
 */
export interface HideableColumn<T> {
  /** the column's label - what the note names when it is hidden. */
  key: string;
  /** the raw value the cell is built from, BEFORE formatting: 0 / null /
   * undefined / '' / '—' are the empties, so pass the number, not "$0.00". */
  get: (r: T) => unknown;
  /** never hidden, whatever the rows hold. */
  fixed?: boolean | undefined;
}

/** What the rule treats as "nothing here": 0, null, undefined, '' and the em-dash cells render for missing values. */
export function isEmptyValue(v: unknown): boolean {
  return v == null || v === 0 || v === '' || v === '—';
}

export function visibleColumns<T, C extends HideableColumn<T>>(
  rows: readonly T[],
  cols: readonly C[],
): { shown: C[]; hidden: string[] } {
  if (rows.length === 0) return { shown: [...cols], hidden: [] };
  const shown: C[] = [];
  const hidden: string[] = [];
  for (const c of cols) {
    if (!c.fixed && rows.every((r) => isEmptyValue(c.get(r)))) hidden.push(c.key);
    else shown.push(c);
  }
  return { shown, hidden };
}

/** The sentence the table note carries; '' when nothing is hidden, so it can be appended unconditionally. */
export function hiddenColumnsNote(hidden: readonly string[]): string {
  return hidden.length ? `Hidden — zero on every row: ${hidden.join(', ')}.` : '';
}
